import { PortalData } from '../generated/api';
import { isEmbeddedFrame } from './frameScope';

declare const storageKeyValue: unique symbol;
export type StorageK<T> = { key: string; persistent: boolean } & { [storageKeyValue]: T };

function _makeKey<T>(key: string, opts?: { persistent?: boolean }): StorageK<T> {
  return { key, persistent: opts?.persistent ?? false } as unknown as StorageK<T>;
}

export function makeKey<T>(key: string): StorageK<T> {
  return _makeKey(key);
}

/**
 * Safer localStorage implementation
 * typed, with static keys, and persistence across clears
 */
class LocalStorage {
  static Keys = {
    // `transient` marks portal-data resolved from a login-provided tenant on a
    // generic portal host (rather than from the host map) — see portalData.ts
    PortalData: _makeKey<PortalData & { transient?: boolean }>('portal-data', {
      persistent: true,
    }),
    ThemePreference: _makeKey<'light' | 'dark'>('theme-preference'),
  } as const;

  // METHODS

  static set<T>({ key }: StorageK<T>, v: T): void {
    window.localStorage.setItem(key, JSON.stringify(v));
  }

  /**
   * List all key (static and dynamic)
   */
  static keys() {
    let staticKeys = Object.values(this.Keys) as StorageK<unknown>[];
    const dynamicKeys = Object.keys(window.localStorage).map(
      (key) => ({ key, persistent: false }) as StorageK<unknown>,
    );
    let missing: StorageK<unknown>[] = [];
    for (let key of dynamicKeys) {
      const match = staticKeys.find((it) => it.key === key.key);
      if (!match) {
        missing.push(key);
      }
    }
    return [...staticKeys, ...dynamicKeys];
  }

  static get<T>({ key }: StorageK<T>): T | undefined {
    try {
      // reading `window.localStorage` itself throws when storage is blocked
      // (embedded frame / blocked cookies), so it has to be inside the try
      const str = window.localStorage.getItem(key);
      if (!str) {
        return;
      }
      return JSON.parse(str) as T;
    } catch (e) {
      return undefined;
    }
  }

  /**
   * clears localStorage while keeping persistent static keys
   */
  static clear() {
    // localStorage is shared with the parent portal when running embedded —
    // a frame logout must not wipe the parent's persisted session
    if (isEmbeddedFrame) return;
    const backup: Record<string, string | null> = {};
    for (let { key } of Object.values(this.Keys)) {
      backup[key] = window.localStorage.getItem(key);
    }
    window.localStorage.clear();

    for (let [key, val] of Object.entries(backup)) {
      if (val !== null) window.localStorage.setItem(key, val);
    }
  }

  static remove<T>({ key }: StorageK<T>): void {
    window.localStorage.removeItem(key);
  }
}

export default LocalStorage;
